import React, { useEffect, useState, useCallback } from 'react';
import { Flex, Card, Statistic, Spin, Typography, Badge } from 'antd';
import {
    ClockCircleOutlined,
    FieldTimeOutlined,
    RiseOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import WaterService from '../../../../services/WaterService';

const { Text } = Typography;

const REFRESH_INTERVAL = 60000;

const formatTime = (isoString) => {
    if (!isoString) return '—';
    return dayjs(isoString).format('DD.MM.YYYY HH:mm');
};

const formatChartTime = (isoString) => {
    if (!isoString) return '';
    return dayjs(isoString).format('DD.MM HH:mm');
};

const cardStyle = {
    borderRadius: 16,
    boxShadow: '0 2px 16px rgba(0,80,179,0.08)',
    border: '1px solid #e6f0ff',
    height: '100%',
};

const metricIconStyle = (color) => ({
    fontSize: 32,
    color,
    marginBottom: 8,
});

const TalasDashboard = () => {
    const { user } = useSelector((state) => state.user);
    const navigate = useNavigate();

    const [summary, setSummary] = useState(null);
    const [dynamicChart, setDynamicChart] = useState([]);
    const [flowSensors, setFlowSensors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate('/login', { replace: true });
        }
    }, [user, navigate]);

    const fetchAll = useCallback(async () => {
        try {
            const [summaryData, dynamicData, flowData] = await Promise.all([
                WaterService.getTalasDashboardSummary(),
                WaterService.getTalasDynamicChart(200),
                WaterService.getTalasFlowSensorsChart(200),
            ]);
            setSummary(summaryData);
            setDynamicChart(dynamicData);
            setFlowSensors(flowData);
            setLastRefresh(new Date());
        } catch (e) {
            // silently keep old data on refresh errors
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
        const timer = setInterval(fetchAll, REFRESH_INTERVAL);
        return () => clearInterval(timer);
    }, [fetchAll]);

    if (loading) {
        return (
            <Flex justify="center" align="center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </Flex>
        );
    }

    const uptime = summary?.uptime;
    const last = summary?.lastMeasurement;
    const maxVal = summary?.maxValues;

    return (
        <Flex vertical gap={24} style={{ width: '100%', padding: '4px 0' }}>

            {/* Auto-refresh indicator */}
            <Flex justify="flex-end" align="center" gap={8}>
                <ReloadOutlined style={{ color: '#52c41a', fontSize: 13 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Обновление каждые 60 сек.
                    {lastRefresh && ` · Последнее: ${dayjs(lastRefresh).format('HH:mm:ss')}`}
                </Text>
            </Flex>

            {/* Row 1 — 3 stat cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 20,
            }}>

                {/* Card 1 — Uptime */}
                <Card style={cardStyle} bodyStyle={{ padding: '24px 28px' }}>
                    <Flex vertical align="flex-start" gap={4}>
                        <ClockCircleOutlined style={metricIconStyle('#0050b3')} />
                        <Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>
                            Время работы системы
                        </Text>
                        <div style={{ fontSize: 28, fontWeight: 800, color: '#0050b3', lineHeight: 1.2, margin: '6px 0' }}>
                            {uptime?.formatted ?? '—'}
                        </div>
                        <Flex gap={16} wrap="wrap">
                            <Statistic
                                title="Первый замер"
                                value={formatTime(uptime?.firstRecordTime)}
                                valueStyle={{ fontSize: 13, color: '#595959' }}
                            />
                            <Statistic
                                title="Всего замеров"
                                value={summary?.totalRecords ?? '—'}
                                valueStyle={{ fontSize: 13, color: '#595959' }}
                            />
                        </Flex>
                    </Flex>
                </Card>

                {/* Card 2 — Last measurement */}
                <Card style={cardStyle} bodyStyle={{ padding: '24px 28px' }}>
                    <Flex vertical align="flex-start" gap={4}>
                        <FieldTimeOutlined style={metricIconStyle('#13c2c2')} />
                        <Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>
                            Последний замер
                        </Text>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#13c2c2', margin: '6px 0' }}>
                            {formatTime(last?.time)}
                        </div>
                        {last ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '4px 16px',
                                marginTop: 8,
                            }}>
                                {[
                                    { label: 'Расход ср.', value: `${last.flowAVG} м³/с` },
                                    { label: 'Скорость ср.', value: `${last.velocityAVG} м/с` },
                                    { label: 'Замер №', value: last.waterId },
                                    { label: 'Темп. (д1)', value: `${last.temp1} °C` },
                                ].map(({ label, value }) => (
                                    <Flex key={label} vertical gap={0}>
                                        <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
                                        <Text strong style={{ fontSize: 14 }}>{value}</Text>
                                    </Flex>
                                ))}
                            </div>
                        ) : (
                            <Text type="secondary">Нет данных</Text>
                        )}
                    </Flex>
                </Card>

                {/* Card 3 — Max values */}
                <Card style={cardStyle} bodyStyle={{ padding: '24px 28px' }}>
                    <Flex vertical align="flex-start" gap={4}>
                        <RiseOutlined style={metricIconStyle('#fa8c16')} />
                        <Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>
                            Максимальные значения за весь период
                        </Text>
                        <div style={{ fontSize: 28, fontWeight: 800, color: '#fa8c16', lineHeight: 1.2, margin: '6px 0' }}>
                            {maxVal?.maxFlowAVG != null ? `${maxVal.maxFlowAVG} м³/с` : '—'}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11, marginBottom: 8 }}>Макс. средний расход</Text>
                        {maxVal && (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '4px 16px',
                            }}>
                                {[
                                    { label: 'Датчик 1', value: maxVal.maxFlow1 },
                                    { label: 'Датчик 2', value: maxVal.maxFlow2 },
                                    { label: 'Датчик 3', value: maxVal.maxFlow3 },
                                    { label: 'Датчик 4', value: maxVal.maxFlow4 },
                                ].map(({ label, value }) => (
                                    <Flex key={label} vertical gap={0}>
                                        <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
                                        <Text strong style={{ fontSize: 14 }}>
                                            {value != null ? `${value} м³/с` : '—'}
                                        </Text>
                                    </Flex>
                                ))}
                            </div>
                        )}
                    </Flex>
                </Card>
            </div>

            {/* Row 2 — Dynamic chart */}
            <Card
                title={
                    <Flex align="center" gap={8}>
                        <Badge color="#0050b3" />
                        <span style={{ fontWeight: 700 }}>Динамика показаний (расход и скорость)</span>
                    </Flex>
                }
                style={cardStyle}
            >
                {dynamicChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dynamicChart} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="time"
                                tickFormatter={formatChartTime}
                                tick={{ fontSize: 11 }}
                                interval="preserveStartEnd"
                            />
                            <YAxis yAxisId="flow" orientation="left" tick={{ fontSize: 11 }} width={55}
                                label={{ value: 'м³/с', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                            <YAxis yAxisId="velocity" orientation="right" tick={{ fontSize: 11 }} width={50}
                                label={{ value: 'м/с', angle: 90, position: 'insideRight', fontSize: 11 }} />
                            <Tooltip
                                labelFormatter={(label) => formatTime(label)}
                                formatter={(value, name) => [
                                    value != null ? value.toFixed(3) : '—',
                                    name === 'flowAVG' ? 'Расход ср. (м³/с)' : 'Скорость ср. (м/с)',
                                ]}
                            />
                            <Legend formatter={(value) =>
                                value === 'flowAVG' ? 'Расход ср. (м³/с)' : 'Скорость ср. (м/с)'
                            } />
                            <Line
                                yAxisId="flow"
                                type="monotone"
                                dataKey="flowAVG"
                                stroke="#0050b3"
                                dot={false}
                                strokeWidth={2}
                            />
                            <Line
                                yAxisId="velocity"
                                type="monotone"
                                dataKey="velocityAVG"
                                stroke="#13c2c2"
                                dot={false}
                                strokeWidth={2}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Flex justify="center" align="center" style={{ height: 300 }}>
                        <Text type="secondary">Нет данных для графика</Text>
                    </Flex>
                )}
            </Card>

            {/* Row 3 — Flow sensors chart */}
            <Card
                title={
                    <Flex align="center" gap={8}>
                        <Badge color="#fa8c16" />
                        <span style={{ fontWeight: 700 }}>Расходчики — все датчики (flow1–flow4)</span>
                    </Flex>
                }
                style={cardStyle}
            >
                {flowSensors.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={flowSensors} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="time"
                                tickFormatter={formatChartTime}
                                tick={{ fontSize: 11 }}
                                interval="preserveStartEnd"
                            />
                            <YAxis tick={{ fontSize: 11 }} width={55}
                                label={{ value: 'м³/с', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                            <Tooltip
                                labelFormatter={(label) => formatTime(label)}
                                formatter={(value, name) => [
                                    value != null ? value.toFixed(3) : '—',
                                    `Датчик ${name.replace('flow', '')} (м³/с)`,
                                ]}
                            />
                            <Legend formatter={(value) => `Датчик ${value.replace('flow', '')}`} />
                            {['flow1', 'flow2', 'flow3', 'flow4'].map((key, i) => (
                                <Line
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={['#0050b3', '#13c2c2', '#52c41a', '#fa8c16'][i]}
                                    dot={false}
                                    strokeWidth={2}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Flex justify="center" align="center" style={{ height: 300 }}>
                        <Text type="secondary">Нет данных для графика</Text>
                    </Flex>
                )}
            </Card>
        </Flex>
    );
};

export default TalasDashboard;
